import React, { Component } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import lodashPick from 'lodash/fp/pick';
import lodashCompact from 'lodash/fp/compact';

import {
  isUserAgentMobile,
} from 'common/util/isUserAgentMobileApp';

import { stateOptionItems } from 'common/util/stateCodes';

import pointInsidePolygon from 'common/util/pointInsidePolygon';
import addressStreetSuffixes from 'common/addressStreetSuffixes';
import capitalize from 'common/util/capitalize';

import {
  formatPrice,
} from 'common/formatters';

import {
  hasRoleLandlord,
} from 'common/userRoles';

import {
  MARKETING_PARTNER_LISTING_STATUS_LANDLORD_INVITED,
  MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS,
  AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_CALL_TO_SCHEDULE,
  AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_OPTION_ITEMS,
  MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_AGENT,
  MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM,
  PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY,
  PROPERTY_SETUP_ACCESS_TYPE_HIDDEN,
  PROPERTY_SETUP_ACCESS_TYPE_SUPRA_LOCKBOX,
  PROPERTY_SETUP_ACCESS_TYPE_USE_ACCESS_CODE,
  PROPERTY_SETUP_ACCESS_TYPE_OPTION_ITEMS,
  PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY,
  PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE,
  PROPERTY_SETUP_ACCESS_METHOD_OPTION_ITEMS,
  PROPERTY_SETUP_ACCESS_TYPE_COMBO_LOCKBOX,
} from 'common/marketingPartnerListingConstants';

import {
  cleanMarketingPartnerListingFormValues,
} from 'common/marketingPartnerListingHelpers';

import {
  LISTING_ERROR_DUPLICATE_ADDRESS,
  LISTING_OCCUPANT_TYPES,
  LISTING_OCCUPANT_TYPE_OWNER,
  LISTING_OCCUPANT_TYPE_VACANT,
  LISTING_VIRTUAL_3D_TOUR_COST,
} from 'common/listingConstants';

import {
  makeListingAddressStreetDisplay,
  makeListingFullAddressString,
  getMarketSecurityDepositLabel,
} from 'common/listingHelpers';

import {
  PROPERTY_TYPE_OPTIONS_PARTNER,
} from 'common/propertyTypes';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import { PARTNER_TYPE_AGENT } from 'common/partnerConstants';

import {
  SR_API_ACTION_PARTNER_CREATE_MARKETING_LISTING,
  SR_API_ACTION_PARTNER_UPDATE_MARKETING_LISTING,
  SR_API_ACTION_GOOGLE_MAPS_GET_AUTOCOMPLETE_PREDICTIONS,
  SR_API_ACTION_GOOGLE_MAPS_GET_PLACE_DETAILS,
  SR_API_ACTION_PARTNER_SEARCH_LANDLORDS,
  SR_API_ACTION_PARTNER_GENERATE_REFERRAL_AGREEMENT_PDF,
} from 'api/actionTypes';

import log from 'client/lib/log';
import petWeightOptionItems, {
  PET_WEIGHT_OPTION_ITEM_NONE,
} from 'client/helpers/petWeightOptionItems';

import {
  extractListingAddressFieldsFromGooglePlace,
} from 'client/lib/googlePlaces';

import postWithState from 'client/actions/postWithState';
import Navigator from 'client/actions/Navigator';
import MobileApp from 'client/actions/MobileApp';

import {
  hideFieldIf,
} from 'client/helpers/formikHelpers';
import {
  SR_CHECKBOX_THEME_BOX,
} from 'client/components/Checkbox';
import {
  BUTTON_THEME_GREY_ADOBE,
  BUTTON_THEME_GREEN_ADOBE,
} from 'client/components/ButtonDeprecated';
import SrBox from 'client/components/SrBox';
import SrText from 'client/components/SrText';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrMultiStepForm, {
  SR_MULTI_STEP_FORM_THEME_PARTNER_PAGE,
} from 'client/components/SrMultiStepForm';
import SrInformationLabel from 'client/components/SrInformationLabel/SrInformationLabel';
import SrSunlocksSelfTouringModal from 'client/components/SrSunlocksSelfTouringModal/SrSunlocksSelfTouringModal';
import SrSupraCbsModal from 'client/components/SrSupraCbsModal/SrSupraCbsModal';
import SrInfoSimpleChecklist from 'client/components/SrInfoSimpleChecklist/SrInfoSimpleChecklist';
import SrPageFooter from './SrPageFooter';
import SrPageFooterYesNo from './SrPageFooterYesNo';
import SrPageFooterSkipLink from './SrPageFooterSkipLink';
import SrLeaseAgreement from './SrLeaseAgreement';
import SrPartnerMarketListingFormStepSimple from './SrPartnerMarketListingFormStepSimple';
import SrOccupancy from './SrOccupancy';
import SrAddressConfirmation from './SrAddressConfirmation';
import SrCommissionField from './SrCommissionField';
import SrPropertySummary from './SrPropertySummary';
import SrListingTermsOfService from './SrListingTermsOfService';
import SrListingAgreement from './SrListingAgreement';
import SrAddressInput from './SrAddressInput';
import SrMarketingItems from './SrMarketingItems';
import SrMarketingItemsLandlord from './SrMarketingItemsLandlord';

function makeFormStepsGetterFunc({ isLandlord, partner, marketingPartnerListing, formIsSubmitting }) {
  return ({ values }) => {
    const isSunroom = (
      values.listingAgreementSignedBy === MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM
    );
    const addressStreetDisplay = makeListingFullAddressString(values);

    const steps = [
      {
        id: 'leaseAgreement',
        component: SrLeaseAgreement,
        ui: {
          navbarTitle: 'Market Property',
          pageHeader: 'How should we work together on this lease?',
          controlComponent: null,
          hideProgressBar: true,
          showHelpCards: true,
        },
        fieldIds: [],
      },
      {
        id: 'address',
        component: SrAddressInput,
        ui: {
          navbarTitle: 'Market Property',
          pageHeader: 'What property are you listing for lease?',
        },
        fieldIds: [
          'propertyType',
          'addressStreetDisplay',
          'apartmentUnit',
          'addressStreetNumber',
          'addressStreetName',
          'addressCity',
          'addressStateCode',
          'addressZip',
          'locationLatitude',
          'locationLongitude',
          !isLandlord && !isSunroom && 'clientWantsPropertyManagement',
        ],
        serverValidations: [
          {
            name: 'addressIsUnique',
            args: [
              'apartmentUnit',
              'addressStreetNumber',
              'addressStreetName',
              'addressCity',
              'addressStateCode',
              'addressZip',
              'partnerId',
            ],
            errorMessage: LISTING_ERROR_DUPLICATE_ADDRESS,
            fieldIdError: 'apartmentUnit',
          },
        ],
      },
      {
        id: 'addressConfirmation',
        component: SrAddressConfirmation,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'Does this location look correct?',
          controlComponent: SrPageFooterYesNo,
        },
        fieldIds: [],
      },
      {
        id: 'unitPrice',
        component: SrPartnerMarketListingFormStepSimple,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'How should we price the unit?',
          controlComponent: SrPageFooterSkipLink,
        },
        fieldIds: [
          'price',
          'securityDeposit',
        ],
      },
      {
        id: 'commission',
        component: SrCommissionField,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'How much commission will your client pay?',
          pageIntroText: 'Sunroom bills your client & pays you a referral. ' +
            'The commission is calculated from 1st month\'s rent.',
        },
        fieldIds: [
          'commissionPercentClientPays',
        ],
      },
      {
        id: 'landlord',
        component: SrPartnerMarketListingFormStepSimple,
        ui: {
          navbarTitle: 'Market Property',
          pageHeader: 'What is your client’s contact info?',
          pageIntroText: isSunroom
            ? 'Sunroom will reach out directly to the property owner to get this listing leased.'
            : 'Your client may get notifications to review applications, and sign the lease.',
        },
        fieldIds: [
          'landlordName',
          'landlordPhone',
          'landlordEmail',
          'landlordNotificationsEnabled',
        ],
      },
      {
        id: 'occupant',
        component: SrOccupancy,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'Who occupies the property?',
          pageIntroText: 'This is posted to the MLS for buyer agents to schedule showings.',
          hideBackButton: isLandlord,
          showHelpCards: true,
        },
        fieldIds: lodashCompact([
          'occupantType',
          'vacantDate',
          'occupantName',
          'occupantPhone',
          'occupantEmail',
          isLandlord && 'clientWantsPropertyManagement',
        ]),
      },
      {
        id: 'marketingItems',
        component: !isLandlord && !isSunroom ? SrMarketingItems : SrMarketingItemsLandlord,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'What marketing items do you want?',
        },
        fieldIds: lodashCompact([
          'yardSignAllowed',
          !isLandlord && 'requireSunlock',
          'noRequireSunlockReason',
          !isLandlord && !isSunroom && 'landlordPropertyAccessNotificationAlert',
          !isLandlord && !isSunroom && 'landlordNotificationAlert',
          !isLandlord && !isSunroom && 'landlordName',
          !isLandlord && !isSunroom && 'landlordPhone',
          !isLandlord && !isSunroom && 'landlordEmail',
          'requirePhotos',
          'linkToExistingPhotos',
          'requireVirtual3dTour',
          'availableForPhotoDate',
          'availableForPhotoDateSchedule',
          'availableForPhotoContactName',
          'availableForPhotoContactPhone',
        ]),
      },
      {
        id: 'availability',
        component: SrPartnerMarketListingFormStepSimple,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'When will this property be accessible?',
          pageIntroText: 'The property must be clean and have the electricity turned on.',
        },
        fieldIds: [
          'availableForSetupDate',
          'availableDate',
        ],
      },
      {
        id: 'access',
        component: SrPartnerMarketListingFormStepSimple,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'How can Sunroom access the property?',
        },
        fieldIds: [
          'accessMethod',
          'propertySetupAccessType',
          'keyPickupDescription',
          'propertyAccessCode',
          'lockboxCode',
          'supraCbsCode',
          'dateKeyExpectedAtListing',
          'gateCode',
          'alarmCode',
          'propertyAccessContactName',
          'propertyAccessContactPhone',
          'propertyAccessContactEmail',
        ],
      },
      {
        id: 'price',
        component: SrPartnerMarketListingFormStepSimple,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'How should we price the unit?',
          pageIntroText: isLandlord && marketingPartnerListing && (marketingPartnerListing.price || marketingPartnerListing.securityDeposit) // eslint-disable-line max-len
            ? `${partner.displayName} recommended the price below.`
            : null,
        },
        fieldIds: [
          'price',
          'securityDeposit',
        ],
      },
      {
        id: 'size',
        component: SrPartnerMarketListingFormStepSimple,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'Property Build',
          pageIntroText: 'This step helps us list the property accurately. '
            + 'We will find the size of the property from a past MLS listing '
            + 'if you don\'t know this information off-hand.',
        },
        fieldIds: [
          'yearBuilt',
          'yearRenovated',
          'bedrooms',
          'bathrooms',
          'squareFeet',
          'legalDescription',
        ],
      },
      {
        id: 'pets',
        component: SrPartnerMarketListingFormStepSimple,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'Are pets allowed?',
          pageIntroText: 'Fair Housing requires that you allow ESA pets.',
        },
        fieldIds: [
          'catsAllowed',
          'dogsAllowed',
          'maximumPetsAllowed',
          'petFee',
          'petDeposit',
          'monthlyPetRent',
          'petWeight',
          'petDepositPerPet',
          'additionalPetDeposit',
          'petFeePerPet',
          'additionalPetFee',
          'monthlyPetRentPerPet',
          'additionalMonthlyPetRent',
        ],
      },
      {
        id: 'notes',
        component: SrPartnerMarketListingFormStepSimple,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'Anything else Sunroom should know?',
        },
        fieldIds: [
          'partnerMarketingNotes',
        ],
      },
      {
        id: 'termsOfService',
        component: SrListingTermsOfService,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'Referral Agreement',
        },
        fieldIds: [
          'agreementForReferralLease',
        ],
      },
      {
        id: 'listingAgreement',
        component: SrListingAgreement,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'Terms of Service',
        },
        fieldIds: [
          'agreementForLeasingRights',
        ],
      },
      {
        id: 'summary',
        component: SrPropertySummary,
        ui: {
          navbarTitle: addressStreetDisplay,
          pageHeader: 'Summary',
          controlComponent: SrPageFooter,
          controlButtonText: formIsSubmitting
            ? 'Submitting Listing...'
            : (
              !marketingPartnerListing
              || (
                marketingPartnerListing.status === MARKETING_PARTNER_LISTING_STATUS_LANDLORD_INVITED
                && isLandlord
              )
            )
              ? 'Submit Property for Leasing'
              : 'Save',
        },
        fieldIds: [],
      },
    ];

    const stepsById = steps.reduce((memo, step) => ({
      ...memo,
      [step.id]: step,
    }), {});

    const sunroomSteps = lodashCompact([
      'leaseAgreement',
      'landlord',
      'address',
      !values.manualAddressInput && 'addressConfirmation',
      'unitPrice',
      'commission',
      'summary',
    ]);

    const agentSteps = lodashCompact([
      'leaseAgreement',
      'address',
      !values.manualAddressInput && 'addressConfirmation',
      'occupant',
      'marketingItems',
      'availability',
      'access',
      'price',
      'size',
      'pets',
      'notes',
      !partner.referralAgreementSignedDate && partner.type === PARTNER_TYPE_AGENT
        ? 'termsOfService'
        : null,
      'summary',
    ]);

    const landlordSteps = [
      'occupant',
      'marketingItems',
      'availability',
      'access',
      'price',
      'size',
      'pets',
      'notes',
      !partner.listingAgreementSignedDate
        ? 'listingAgreement'
        : null,
      'summary',
    ];

    const selectedSteps = isLandlord
      ? landlordSteps
      : (isSunroom ? sunroomSteps : agentSteps);

    const formSteps = selectedSteps.map((stepId) => ({
      ...stepsById[stepId],
      ui: {
        theme: SR_MULTI_STEP_FORM_THEME_PARTNER_PAGE,
        controlComponent: SrPageFooter,
        ...stepsById[stepId].ui,
      },
    }));

    return formSteps;
  };
}

function makeFormFieldsGetterFunc({
  isLandlord,
  isMobile,
  googleAddressMatches,
  onGoogleAddressChange,
  onFindGoogleListings,
  landlordSuggestionChoices,
  onLandlordChange,
  onRequireSunlockChange,
  onFindLandlords,
  onOcuppantTypeChange,
  onAccessMethodChange,
  onOpenSupraCbsModal,
  onOpenSunlockSelfTouringModal,
  marketsList,
}) {
  return ({ values = {} } = {}) => {
    const isSunroom = (
      values.listingAgreementSignedBy === MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM
      && !isLandlord
    );

    const shouldShowOccupantFields = (
      values.occupantType
      && values.occupantType !== LISTING_OCCUPANT_TYPE_VACANT
    );
    const shouldShowPetFields = values.dogsAllowed || values.catsAllowed;

    const shouldShowKeyContact = values.accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY
      && values.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY;

    const shouldShowScheduleContact = (
      (
        values.requirePhotos
        || values.requireVirtual3dTour
      )
      && values.availableForPhotoDateSchedule === AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_CALL_TO_SCHEDULE
    );

    const currentYear = moment().year();
    const earliestYearOption = 1949;
    const recentlyRenovated = (currentYear - values.yearRenovated) <= 2;
    const recentlyBuilt = (currentYear - values.yearBuilt) <= 2;

    let showVirtual3dTourOptIn = false;
    let securityDepositLabel = getMarketSecurityDepositLabel(null, true);

    if (values.locationLongitude && values.locationLatitude) {
      for (const market of marketsList) {
        const point = [values.locationLongitude, values.locationLatitude];
        if (pointInsidePolygon(point, market.serviceZone.coordinates)) {
          securityDepositLabel = getMarketSecurityDepositLabel(market, true);
          showVirtual3dTourOptIn = market.virtual3dTourOptInEnabled;
          break;
        }
      }
    }

    return [
      {
        id: 'listingAgreementSignedBy',
        title: '',
        type: 'select',
        properties: {
          choices: [
            MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM,
            MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_AGENT,
          ],
        },
        validations: ['required'],
      },
      {
        id: 'propertyType',
        title: 'Property Type',
        type: 'select',
        properties: {
          choices: PROPERTY_TYPE_OPTIONS_PARTNER.map((listingType) => ({
            label: listingType,
            value: listingType,
          })),
        },
        validations: ['required'],
      },
      {
        id: 'manualAddressInput',
        type: 'hidden',
      },
      hideFieldIf(values.manualAddressInput, {
        id: 'addressStreetDisplay',
        title: 'Address',
        subtitle: '(must select dropdown option)',
        type: 'autocomplete',
        validations: lodashCompact([
          'required',
          (!values.locationLatitude || !values.locationLongitude) && 'useManualAddressInput',
        ]),
        properties: {
          placeholder: 'Write address...',
          minCharMatchCount: 1,
          choices: googleAddressMatches.map(({ description }) => description),
          onSuggestionsFetchRequested: ({ value }) => {
            return onFindGoogleListings({ ...values, addressStreetDisplay: value });
          },
          onChange: onGoogleAddressChange,
          filterFunc: () => {
            return true;
          },
        },
      }),
      hideFieldIf(!values.manualAddressInput, {
        id: 'addressStreetNumber',
        type: 'short_text',
        title: 'Street Number',
        properties: {
          placeholder: 'Street Number',
        },
        validations: ['required'],
      }),
      hideFieldIf(!values.manualAddressInput, {
        id: 'addressStreetName',
        type: 'short_text',
        title: 'Street Name',
        properties: {
          placeholder: 'Street Name',
        },
        validations: ['required'],
      }),
      {
        id: 'addressStreetSuffix',
        type: 'hidden',
      },
      hideFieldIf(!values.manualAddressInput, {
        id: 'addressCity',
        type: 'short_text',
        title: 'City',
        properties: {
          placeholder: 'City',
        },
        validations: ['required'],
      }),
      hideFieldIf(!values.manualAddressInput, {
        id: 'addressStateCode',
        type: 'select',
        title: 'State',
        properties: {
          choices: stateOptionItems,
          enableResetOption: true,
        },
        validations: ['required'],
      }),
      hideFieldIf(!values.manualAddressInput, {
        id: 'addressZip',
        type: 'number',
        title: 'ZIP Code',
        properties: {
          placeholder: 'ZIP Code',
        },
        validations: ['required'],
      }),
      {
        id: 'locationLatitude',
        type: 'hidden',
      },
      {
        id: 'locationLongitude',
        type: 'hidden',
      },
      {
        id: 'apartmentUnit',
        title: 'Unit #',
        titleSecondary: '(optional)',
        type: 'short_text',
        properties: {
          placeholder: 'Unit #',
        },
      },
      hideFieldIf(isSunroom, {
        id: 'occupantType',
        title: 'Occupant Status',
        type: 'multiple_choice',
        properties: {
          choices: LISTING_OCCUPANT_TYPES.map((listingType) => ({
            label: listingType,
            value: listingType,
          })),
          multipleSelection: false,
          fieldType: SR_CHECKBOX_THEME_BOX,
          onChange: onOcuppantTypeChange,
        },
        validations: ['required'],
      }),
      hideFieldIf(!isSunroom, {
        id: 'commissionPercentClientPays',
        type: 'percent',
        validations: ['required'],
      }),
      hideFieldIf(isSunroom || !shouldShowOccupantFields, {
        id: 'vacantDate',
        title: 'When will this property become vacant?',
        type: 'date_picker',
        properties: {
          placeholderValue: 'MM/DD/YYYY',
          placeholder: 'MM/DD/YYYY',
          dateFormat: 'MM/DD/YYYY',
          readOnly: false,
        },
        validations: ['required'],
      }),
      hideFieldIf(isSunroom || !shouldShowOccupantFields, {
        id: 'occupantName',
        title: `${values.occupantType} full name`,
        subtitle: `${values.occupantType}'s name & phone will be placed in the MLS for buyer agents to schedule showings.`, // eslint-disable-line max-len
        type: 'short_text',
        properties: {
          placeholder: `${values.occupantType} full name`,
        },
        validations: ['required'],
      }),
      hideFieldIf(isSunroom || !shouldShowOccupantFields, {
        id: 'occupantPhone',
        title: `${values.occupantType} phone`,
        subtitle: `${values.occupantType}'s name & phone will be placed in the MLS for buyer agents to schedule showings.`, // eslint-disable-line max-len
        type: 'phone',
        properties: {
          placeholder: `${values.occupantType} phone`,
          pattern: '[0-9]*',
        },
        validations: ['required', 'phone'],
      }),
      hideFieldIf(isSunroom || !shouldShowOccupantFields, {
        id: 'occupantEmail',
        title: `${values.occupantType} email`,
        titleSecondary: '(Optional)',
        type: 'email',
        properties: {
          placeholder: `${values.occupantType} email`,
        },
        validations: ['email'],
      }),
      hideFieldIf(isSunroom, {
        id: 'availableForSetupDate',
        title: 'Earliest day Sunroom can install marketing items?',
        subtitle: '(Yard Sign, Supra, Combo Lockbox, etc.)',
        type: 'date_picker',
        properties: {
          placeholderValue: 'MM/DD/YYYY',
          placeholder: 'MM/DD/YYYY',
          dateFormat: 'MM/DD/YYYY',
          readOnly: isMobile,
          isValidDate: (date) => date.isAfter(momentWithAustinTimezone().subtract(1, 'days')),
        },
        validations: ['required'],
      }),
      hideFieldIf(isSunroom, {
        id: 'availableDate',
        title: 'Earliest a renter can move in?',
        type: 'date_picker',
        properties: {
          placeholderValue: 'MM/DD/YYYY',
          placeholder: 'MM/DD/YYYY',
          dateFormat: 'MM/DD/YYYY',
          readOnly: isMobile,
          isValidDate: (date) => date.isAfter(momentWithAustinTimezone().subtract(1, 'days')),
        },
        validations: ['required'],
      }),
      hideFieldIf(isSunroom, {
        id: 'accessMethod',
        title: 'Access method',
        type: 'multiple_choice',
        properties: {
          multipleSelection: false,
          useArrayValues: false,
          choices: PROPERTY_SETUP_ACCESS_METHOD_OPTION_ITEMS,
          fieldType: SR_CHECKBOX_THEME_BOX,
          autoFitWidth: '400px',
          onChange: onAccessMethodChange,
        },
        validations: ['required'],
      }),
      hideFieldIf(isSunroom
        || values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY,
      {
        id: 'propertySetupAccessType',
        title: 'Key location',
        type: 'multiple_choice',
        properties: {
          multipleSelection: false,
          useArrayValues: false,
          choices: PROPERTY_SETUP_ACCESS_TYPE_OPTION_ITEMS,
          fieldType: SR_CHECKBOX_THEME_BOX,
          autoFitWidth: '400px',
        },
        validations: ['required'],
        additionalComponent: (
          values.accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY
          && values.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY)
          ? (
            <SrBox marginTop='16px'>
              <SrInformationLabel
                text={(
                  <SrText
                    font='greycliff'
                    color='blueGreycliff'
                    weight='500'
                    size='14'
                    lineHeight='20px'
                  >
                    When Sunroom sets up your property for marketing,
                    &#8202;
                    <SrText
                      font='greycliff'
                      color='blueGreycliff'
                      weight='700'
                      size='14'
                      lineHeight='20px'
                    >
                      we will place your key into our combo lockbox.
                    </SrText>
                    &#8202;
                    We will email you the combo code.
                  </SrText>
                )}
              />
            </SrBox>
          )
          : null,
      }),
      hideFieldIf(isSunroom
        || values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY
        || values.propertySetupAccessType !== PROPERTY_SETUP_ACCESS_TYPE_COMBO_LOCKBOX,
      {
        id: 'lockboxCode',
        title: 'Combination Lockbox Code',
        type: 'short_text',
        properties: {
          placeholder: '1234#',
        },
        validations: ['required'],
        additionalComponent: (
          <SrBox marginTop='16px'>
            <SrInformationLabel
              text={(
                <SrText
                  font='greycliff'
                  color='blueGreycliff'
                  weight='500'
                  size='14'
                  lineHeight='20px'
                >
                  When Sunroom sets up your property for marketing,
                  &#8202;
                  <SrText
                    font='greycliff'
                    color='blueGreycliff'
                    weight='700'
                    size='14'
                    lineHeight='20px'
                  >
                    we will place your key into our combo lockbox.
                  </SrText>
                  &#8202;
                  We will email you the combo code.
                </SrText>
              )}
            />
          </SrBox>
        ),
      }),
      hideFieldIf(isSunroom
        || values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY
        || values.propertySetupAccessType !== PROPERTY_SETUP_ACCESS_TYPE_HIDDEN,
      {
        id: 'keyPickupDescription',
        title: 'Where is the key located?',
        type: 'long_text',
        properties: {
          placeholder: 'The key is located...',
        },
        validations: ['required'],
        additionalComponent: (
          <SrBox marginTop='16px'>
            <SrInformationLabel
              text={(
                <SrText
                  font='greycliff'
                  color='blueGreycliff'
                  weight='500'
                  size='14'
                  lineHeight='20px'
                >
                  When Sunroom sets up your property for marketing,
                  &#8202;
                  <SrText
                    font='greycliff'
                    color='blueGreycliff'
                    weight='700'
                    size='14'
                    lineHeight='20px'
                  >
                    we will place your key into our combo lockbox.
                  </SrText>
                  &#8202;
                  We will email you the combo code.
                </SrText>
              )}
            />
          </SrBox>
        ),
      }),
      hideFieldIf(isSunroom
        || values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE
        || values.propertySetupAccessType !== PROPERTY_SETUP_ACCESS_TYPE_USE_ACCESS_CODE,
      {
        id: 'propertyAccessCode',
        title: 'What is the access code?',
        type: 'short_text',
        properties: {
          placeholder: 'Access instructions...',
        },
        validations: ['required'],
        additionalComponent: values.requireSunlock
          ? (
            <SrBox marginTop='16px'>
              <SrInformationLabel
                text={(
                  <SrText
                    font='greycliff'
                    color='blueGreycliff'
                    weight='500'
                    size='14'
                    lineHeight='20px'
                  >
                    When Sunroom sets up your property for marketing,
                    &#8202;
                    <SrText
                      font='greycliff'
                      color='blueGreycliff'
                      weight='700'
                      size='14'
                      lineHeight='20px'
                    >
                      we will uninstall the original electronic lock and replace it with our Sunlock.
                    </SrText>
                    &#8202;
                    We will email you the new access code.
                  </SrText>
                )}
              />
            </SrBox>
          )
          : null,
      }),
      hideFieldIf(isSunroom
        || values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY
        || values.propertySetupAccessType !== PROPERTY_SETUP_ACCESS_TYPE_SUPRA_LOCKBOX,
      {
        id: 'supraCbsCode',
        title: 'Supra Lockbox CBS Code',
        type: 'short_text',
        properties: {
          placeholder: '1234567',
          pattern: '[0-9]*',
        },
        validations: ['required', 'supraCbs'],
        actionLink: {
          text: 'Where do I find the CBS code?',
          onClick: () => onOpenSupraCbsModal(),
        },
        additionalComponent: (
          <SrBox marginTop='16px'>
            <SrInformationLabel
              text={(
                <SrText
                  font='greycliff'
                  color='blueGreycliff'
                  weight='500'
                  size='14'
                  lineHeight='20px'
                >
                  Renters
                  &#8202;
                  <SrText
                    font='greycliff'
                    color='blueGreycliff'
                    weight='700'
                    size='14'
                    lineHeight='20px'
                  >
                    will not
                  </SrText>
                  &#8202;
                  have access to the Supra. Sunroom only needs the Supra CBS code for setting up the property.
                  &#8202;
                  <SrText
                    font='greycliff'
                    color='blueGreycliff'
                    weight='700'
                    size='14'
                    lineHeight='20px'
                  >
                    Sunroom will place your key into our combo lockbox.
                  </SrText>
                  &#8202;
                  We will email you the combo code.
                </SrText>
              )}
            />
          </SrBox>
        ),
      }),
      hideFieldIf(isSunroom
        || values.accessMethod !== PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY
        || values.propertySetupAccessType === PROPERTY_SETUP_ACCESS_TYPE_CONTACT_FOR_KEY
        || !values.propertySetupAccessType, {
        id: 'dateKeyExpectedAtListing',
        title: 'Date the key will be at the listing',
        subtitle: 'If the key is not at the listing on the date indicated, the listing setup may be delayed.',
        type: 'date_picker',
        properties: {
          placeholder: 'MM/DD/YYYY',
        },
        validations: ['required'],
      }),
      hideFieldIf(isSunroom || !shouldShowKeyContact, {
        id: 'propertyAccessContactName',
        title: 'Key Contact Name',
        properties: {
          placeholder: 'Key contact name',
        },
        type: 'short_text',
        validations: ['required'],
      }),
      hideFieldIf(isSunroom || !shouldShowKeyContact, {
        id: 'propertyAccessContactPhone',
        title: 'Key Contact Phone',
        properties: {
          placeholder: 'Key contact phone',
          pattern: '[0-9]*',
        },
        type: 'phone',
        validations: ['required', 'phone'],
      }),
      hideFieldIf(isSunroom || !shouldShowKeyContact, {
        id: 'propertyAccessContactEmail',
        title: 'Key Contact Email',
        properties: {
          placeholder: 'Key contact email',
        },
        type: 'email',
        validations: ['required', 'email'],
      }),
      hideFieldIf(isSunroom || !values.accessMethod || !values.propertySetupAccessType, {
        id: 'gateCode',
        title: 'Gate Code',
        titleSecondary: '(optional)',
        type: 'short_text',
        properties: {
          placeholder: '1234#',
        },
      }),
      hideFieldIf(isSunroom || !values.accessMethod || !values.propertySetupAccessType, {
        id: 'alarmCode',
        title: 'Alarm Code',
        titleSecondary: '(optional)',
        type: 'short_text',
        properties: {
          placeholder: 'Press star, then 9876, then "alarm off"',
        },
      }),
      {
        id: 'price',
        title: 'Rent price',
        titleSecondary: (isSunroom || isLandlord) ? '(optional)' : null,
        type: 'currency',
        properties: {
          placeholder: 'Rent price',
        },
        validations: (isSunroom || isLandlord) ? null : ['required'],
      },
      {
        id: 'yearBuilt',
        title: 'When was this home built?',
        type: 'number',
        properties: {
          placeholder: 'Year built',
          isValid: !values.yearRenovated ? true : values.yearRenovated >= values.yearBuilt,
        },
        validations: ['yearBuilt', 'required'],
      },
      {
        id: 'yearRenovated',
        title: 'When was this home last renovated?',
        type: 'select',
        properties: {
          enableResetOption: true,
          placeholder: 'Never',
          choices: Array.from({ length: currentYear - earliestYearOption }, (v, k) => currentYear - k)
            .map((year) => {
              return {
                label: year,
                value: year,
              };
            }),
        },
      },
      {
        id: 'bedrooms',
        title: 'Beds',
        titleSecondary: recentlyRenovated || recentlyBuilt
          ? null
          : '(optional)',
        type: 'numberWithComma',
        properties: {
          placeholder: 'Number of beds',
        },
        validations: recentlyRenovated || recentlyBuilt
          ? ['required']
          : null,
      },
      {
        id: 'bathrooms',
        title: 'Baths',
        titleSecondary: recentlyRenovated || recentlyBuilt
          ? null
          : '(optional)',
        type: 'numberWithComma',
        properties: {
          placeholder: 'Number of baths',
        },
        validations: recentlyRenovated || recentlyBuilt
          ? ['required']
          : null,
      },
      {
        id: 'squareFeet',
        title: 'Square Feet',
        titleSecondary: recentlyRenovated || recentlyBuilt
          ? null
          : '(optional)',
        subtitle: 'Living area',
        type: 'numberWithComma',
        properties: {
          placeholder: 'sq ft',
        },
        validations: recentlyRenovated || recentlyBuilt
          ? ['required']
          : null,
      },
      {
        id: 'legalDescription',
        title: 'Legal Description of the Property',
        titleSecondary: recentlyRenovated || recentlyBuilt
          ? null
          : '(optional)',
        type: 'long_text',
        properties: {
          placeholder: 'Write legal description here...',
        },
        validations: recentlyRenovated || recentlyBuilt
          ? ['required']
          : null,
      },
      {
        id: 'securityDeposit',
        title: securityDepositLabel,
        titleSecondary: (isSunroom || isLandlord) ? '(optional)' : null,
        type: 'currency',
        properties: {
          placeholder: securityDepositLabel,
        },
        validations: (isSunroom || isLandlord) ? null : ['required'],
      },
      hideFieldIf(isSunroom, {
        id: 'yardSignAllowed',
        title: 'Yard Sign allowed?',
        type: 'yes_no',
        validations: ['required'],
      }),
      hideFieldIf(isSunroom, {
        id: 'requireSunlock',
        title: 'Install Sunlock for self tours?',
        label: '(It\'s only installed once the property is vacant.)',
        type: 'yes_no',
        properties: {
          onChange: onRequireSunlockChange,
        },
        actionLink: {
          text: '🔑 See how Sunlocks increase tours',
          onClick: () => onOpenSunlockSelfTouringModal(),
        },
        additionalComponent: values.requireSunlock === false
          ? (
            <>
              <SrText
                font='greycliff'
                color='blueGreycliff'
                weight='800'
                size='21'
                lineHeight='25px'
              >
                We highly recommend you install a Sunlock, it will get you more tours.
              </SrText>
              <SrBox marginTop='16px'>
                <SrInfoSimpleChecklist
                  title='Benefits of installing a Sunlock'
                  listItems={[
                    'Show vacant properties safely',
                    'Drive more tours',
                    'Get more renter demand',
                    'Lease faster',
                  ]}
                />
              </SrBox>
            </>
          ) : null,
        validations: ['required'],
      }),
      hideFieldIf(isSunroom || values.requireSunlock === true || values.requireSunlock === null, {
        id: 'noRequireSunlockReason',
        title: 'Reason for not installing a Sunlock?',
        type: 'long_text',
        validations: ['required'],
      }),
      hideFieldIf(isSunroom, {
        id: 'partnerMarketingNotes',
        title: 'Extra notes',
        titleSecondary: '(optional)',
        type: 'long_text',
        properties: {
          placeholder: 'Extra notes...',
        },
      }),
      hideFieldIf(isSunroom || values.requireSunlock !== true, {
        id: 'landlordPropertyAccessNotificationAlert',
        title: 'Send Sunlock access instructions to your client?',
        subtitle: (
          <>
            <SrText
              weight='800'
              font='greycliff'
              letterSpacing='0'
              color='blueGreycliff'
            >
              Your client's original key won't let them access their
              property
            </SrText>
            &nbsp;
            <SrText
              font='greycliff'
              letterSpacing='0'
              weight='500'
              color='blueGreycliff'
            >
              after the Sunlock has been installed.
            </SrText>
          </>
        ),
        type: 'yes_no',
        validations: ['required'],
      }),
      hideFieldIf(isSunroom, {
        id: 'landlordNotificationAlert',
        title: 'Enable Client Updates',
        subtitle: 'Your client will get notified when the listing goes live, ' +
          'renters tour, renters give feedback, and renters apply!',
        type: 'yes_no',
        validations: ['required'],
      }),
      hideFieldIf(!isSunroom
        && !values.landlordPropertyAccessNotificationAlert
        && !values.landlordNotificationAlert, {
        id: 'landlordName',
        title: 'Client full name',
        type: 'autocomplete',
        properties: {
          placeholder: 'Client full name',
          minCharMatchCount: 2,
          choices: landlordSuggestionChoices,
          onSuggestionsFetchRequested: onFindLandlords,
          onChange: onLandlordChange,
          filterFunc: ({ value, choice }) => {
            const lowerCaseChoice = choice.toLowerCase();
            return lowerCaseChoice.includes(value);
          },
        },
        validations: ['required'],
      }),
      hideFieldIf(!isSunroom
        && !values.landlordPropertyAccessNotificationAlert
        && !values.landlordNotificationAlert, {
        id: 'landlordPhone',
        title: 'Client phone',
        type: 'phone',
        properties: {
          placeholder: 'Client phone',
          pattern: '[0-9]*',
        },
        validations: ['phone', 'required'],
      }),
      hideFieldIf(!isSunroom
        && !values.landlordPropertyAccessNotificationAlert
        && !values.landlordNotificationAlert, {
        id: 'landlordEmail',
        title: 'Client email',
        type: 'email',
        properties: {
          placeholder: 'Client email',
        },
        validations: ['email', 'required'],
      }),
      hideFieldIf(isSunroom, {
        id: 'landlordNotificationsEnabled',
        title: '',
        type: 'multiple_choice',
        properties: {
          multipleSelection: false,
          useArrayValues: false,
          toggleSingleSelection: true,
          uncheckedValue: true,
          choices: [{
            label: 'My client does not want notifications',
            value: false,
          }],
        },
      }),
      hideFieldIf(isSunroom, {
        id: 'requirePhotos',
        title: 'Want Sunroom to take new photos?',
        type: 'yes_no',
        validations: ['required'],
      }),
      hideFieldIf(isSunroom || values.requirePhotos !== false, {
        id: 'linkToExistingPhotos',
        title: 'Link to the photos',
        subtitle: '(dropbox, zillow, etc.)',
        type: 'short_text',
        properties: {
          placeholder: 'Paste the link to your photos...',
        },
        validations: ['required'],
      }),
      hideFieldIf(isSunroom || !showVirtual3dTourOptIn, {
        id: 'requireVirtual3dTour',
        title: 'Want Sunroom to take a 3D Tour?',
        label: '(extra charge)',
        subtitle: (
          'We use the industry standard, Matterport. It costs an additional ' +
          `${formatPrice(LISTING_VIRTUAL_3D_TOUR_COST)}. Charged at lease sign.`
        ),
        type: 'yes_no',
        validations: ['required'],
      }),
      hideFieldIf(isSunroom || (!values.requirePhotos && !values.requireVirtual3dTour), {
        id: 'availableForPhotoDate',
        title: `Earliest day Sunroom can take ${values.requirePhotos ? 'photos' : 'a 3D tour'}?`,
        type: 'date_picker',
        properties: {
          placeholderValue: 'MM/DD/YYYY',
          placeholder: 'MM/DD/YYYY',
          dateFormat: 'MM/DD/YYYY',
          readOnly: isMobile,
        },
        validations: ['required'],
      }),
      hideFieldIf(isSunroom || (!values.requirePhotos && !values.requireVirtual3dTour), {
        id: 'availableForPhotoDateSchedule',
        title: (
          `Can Sunroom go take ${values.requirePhotos ? 'photos' : 'the 3D tour'} ` +
          'any time during the day?'
        ),
        type: 'multiple_choice',
        properties: {
          multipleSelection: false,
          useArrayValues: false,
          choices: AVAILABLE_FOR_PHOTO_DATE_SCHEDULE_OPTION_ITEMS,
          fieldType: SR_CHECKBOX_THEME_BOX,
          autoFitWidth: '250px',
        },
        validations: ['required'],
      }),
      hideFieldIf(isSunroom || !shouldShowScheduleContact, {
        id: 'availableForPhotoContactName',
        title: 'Scheduling Contact name',
        properties: {
          placeholder: 'Scheduling Contact name...',
        },
        type: 'short_text',
        validations: ['required'],
      }),
      hideFieldIf(isSunroom || !shouldShowScheduleContact, {
        id: 'availableForPhotoContactPhone',
        title: 'Scheduling Contact phone',
        properties: {
          placeholder: 'Scheduling Contact phone...',
          pattern: '[0-9]*',
        },
        type: 'phone',
        validations: ['required', 'phone'],
      }),
      hideFieldIf(isSunroom, {
        id: 'catsAllowed',
        title: 'Cats Allowed?',
        type: 'yes_no',
        validations: ['required'],
      }),
      hideFieldIf(isSunroom, {
        id: 'dogsAllowed',
        title: 'Dogs Allowed?',
        type: 'yes_no',
        validations: ['required'],
      }),
      hideFieldIf(isSunroom || !shouldShowPetFields, {
        id: 'maximumPetsAllowed',
        title: 'Max Pets',
        type: 'short_text',
        properties: {
          onlyAcceptNumbers: true,
          placeholder: '2',
          pattern: '[0-9]*',
        },
        validations: ['positiveNumberString'],
      }),
      hideFieldIf(isSunroom || !shouldShowPetFields || !values.dogsAllowed, {
        id: 'petWeight',
        title: 'Max Dog Weight',
        type: 'select',
        properties: {
          transformToNumber: true,
          choices: petWeightOptionItems,
        },
        validations: ['required'],
      }),
      hideFieldIf(isSunroom || !shouldShowPetFields, {
        id: 'petFee',
        title: 'Pet Fee',
        titleSecondary: '(optional)',
        subtitle: '(Non-Refundable)',
        type: 'currency',
        properties: {
          placeholder: '$100',
        },
      }),
      hideFieldIf(isSunroom || !shouldShowPetFields, {
        id: 'petDeposit',
        title: 'Pet Deposit',
        titleSecondary: '(optional)',
        subtitle: '(Refundable)',
        type: 'currency',
        properties: {
          placeholder: '$200',
        },
      }),
      hideFieldIf(isSunroom || !shouldShowPetFields, {
        id: 'monthlyPetRent',
        title: 'Pet Rent',
        titleSecondary: '(optional)',
        type: 'currency',
        properties: {
          placeholder: '$15',
        },
      }),
      hideFieldIf(isSunroom, {
        id: 'clientWantsPropertyManagement',
        title: isLandlord ? 'Do you want property management?' : 'Does your client want property management?',
        type: 'multiple_choice',
        properties: {
          choices: [
            {
              label: 'Yes',
              value: true,
            },
            {
              label: 'No',
              value: false,
            },
            {
              label: 'Not Sure',
              value: 'Not Sure',
            },
          ],
          multipleSelection: false,
          fieldType: SR_CHECKBOX_THEME_BOX,
          autoFitWidth: '410px',
        },
        validations: ['required'],
      }),
      {
        id: 'agreementForReferralLease',
        type: 'simple_checkbox',
        properties: {
          children: `By clicking this checkbox, I'm electronically signing Sunroom’s referral agreement.
          I give Sunroom the exclusive right to lease properties I submit through this portal.`,
          className: 'adobeCheckboxSquare',
        },
        validations: ['required', 'legal'],
      },
      {
        id: 'agreementForLeasingRights',
        type: 'simple_checkbox',
        properties: {
          children: (
            'I agree to give Sunroom Rentals the exclusive right to lease my property. ' +
            'By checking this box I am electronically signing the listing services agreement.'
          ),
          className: 'adobeCheckboxSquare',
        },
        validations: ['required', 'legal'],
      },
      {
        id: 'partnerId',
        type: 'hidden',
      },
      {
        id: 'marketingPartnerListingId',
        type: 'hidden',
      },
      {
        id: 'listingId',
        type: 'hidden',
      },
      {
        id: 'petDepositPerPet',
        type: 'hidden',
      },
      {
        id: 'additionalPetDeposit',
        type: 'hidden',
      },
      {
        id: 'petFeePerPet',
        type: 'hidden',
      },
      {
        id: 'additionalPetFee',
        type: 'hidden',
      },
      {
        id: 'monthlyPetRentPerPet',
        type: 'hidden',
      },
      {
        id: 'additionalMonthlyPetRent',
        type: 'hidden',
      },
    ];
  };
}

function makeLandlordMatchString({ name, phone, email }) {
  return `${name} - ${phone} - ${email}`;
}

class SrPartnerMarketListingForm extends Component {
  static propTypes = {
    onBack: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    partnerId: PropTypes.number.isRequired,
    propertyAttributes: PropTypes.array,
    editMarketingPartnerListing: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    partner: PropTypes.object,
    userAgent: PropTypes.object.isRequired,
    currentUser: PropTypes.object.isRequired,
    helpCards: PropTypes.array,
    marketsList: PropTypes.array.isRequired,
  }

  constructor(props) {
    super(props);
    this.state = {
      googleAddressMatches: [],
      landlordMatches: [],
      shouldShowSuccessModal: false,
      shouldShowSupraCbsModal: false,
      shouldShowSunlockSelfTouringModal: false,
      shouldShowLoadingScreen: false,
      isHandsOffFlow: false,
      mostRecentSubmissionId: null,
      formIsSubmitting: false,
    };
    this._onFindGoogleListings = this._onFindGoogleListings.bind(this);
    this.multiStepFormRef = React.createRef();
  }

  _onSubmit = (formValues) => {
    this.setState({
      formIsSubmitting: true,
    });
    const { dispatch, currentUser, partner, editMarketingPartnerListing } = this.props;
    const isLandlord = hasRoleLandlord(currentUser.roles);
    const updates = cleanMarketingPartnerListingFormValues(formValues);
    const currentDate = momentWithAustinTimezone();
    updates.addressStreetDisplay = makeListingAddressStreetDisplay(formValues);
    if (updates.addressStreetName) {
      const splitAddress = updates.addressStreetName.split(' ');
      const capitalizedLastWord = capitalize(splitAddress[splitAddress.length - 1].toLowerCase());
      const includesSuffix = addressStreetSuffixes.includes(capitalizedLastWord);
      if (includesSuffix) {
        updates.addressStreetSuffix = capitalizedLastWord;
        updates.addressStreetName = splitAddress.slice(0, -1).join(' ');
      }
    }
    if (typeof updates.clientWantsPropertyManagement === 'string') {
      updates.clientWantsPropertyManagement = null;
    }
    if (updates.listingAgreementSignedBy !== MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM) {
      updates.commissionPercentClientPays = null;
    }

    if (
      formValues.marketingPartnerListingId
      && isLandlord
      && editMarketingPartnerListing?.status === MARKETING_PARTNER_LISTING_STATUS_LANDLORD_INVITED
    ) {
      updates.status = MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS;
    }
    if (
      !partner.referralAgreementSignedDate
        && partner.type === PARTNER_TYPE_AGENT
        && formValues.agreementForReferralLease
    ) {
      updates.referralAgreementSignedDate = currentDate.toISOString();
    }

    this.setState({
      isHandsOffFlow:
        updates.listingAgreementSignedBy === MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM,
    });

    if (formValues.listingAgreementSignedBy === MARKETING_PARTNER_LISTING_AGREEMENT_SIGNED_BY_SUNROOM) {
      updates.landlordNotificationAlert = true;
      updates.landlordPropertyAccessNotificationAlert = true;
    }

    const urlParams = new URLSearchParams(global.location.search);
    const renewalListingId = urlParams.get('renewalListingId');

    dispatch(postWithState, {
      action: formValues.marketingPartnerListingId
        ? SR_API_ACTION_PARTNER_UPDATE_MARKETING_LISTING
        : SR_API_ACTION_PARTNER_CREATE_MARKETING_LISTING,
      payload: {
        updates: updates,
        marketingPartnerListingId: formValues.marketingPartnerListingId,
        renewalListingId: renewalListingId ? Number(renewalListingId) : null,
      },
      onSuccess: (res) => {
        this.setState({
          shouldShowSuccessModal: true,
          mostRecentSubmissionId: res.id,
          formIsSubmitting: false,
        });

        if (isLandlord) {
          setTimeout(() => {
            this._askForPushNotifications();
            dispatch(Navigator.replace, {
              to: `/partner/${partner.slug}/listing/${editMarketingPartnerListing.listingId}`,
            });
          }, 3000);
        }
      },
      onError: (error) => {
        log.error(error);
      },
    }).then(() => {
      if (
        !partner.referralAgreementPdfUrl
          && formValues.agreementForReferralLease
          && partner.type === PARTNER_TYPE_AGENT
      ) {
        dispatch(postWithState, {
          action: SR_API_ACTION_PARTNER_GENERATE_REFERRAL_AGREEMENT_PDF,
          payload: {
            partnerId: partner.id,
            currentDate: currentDate.format('MM/DD/YYYY'),
            currentUserEmail: currentUser.email,
            addressStreetDisplay: formValues.addressStreetDisplay,
          },
          onError: (error) => {
            log.error(error);
          },
        });
      }
    });
  }

  _onSuccessModalMarketAnotherProperty = () => {
    // HACK(@d-oliveros): Refresh the marketing flow.
    // TODO(@Sunroom): Figure out a way to reset SrMultiStepForm.
    this.setState({
      shouldShowLoadingScreen: true,
    });
    global.location.reload();
  }

  _onSuccessModalReturnHome = () => {
    const { onSubmit } = this.props;
    this._askForPushNotifications();
    onSubmit(this.state.mostRecentSubmissionId);
  }

  _onFindGoogleListings(formValues) {
    const { dispatch } = this.props;
    if (
      !formValues.addressStreetDisplay
    ) {
      this.setState({
        googleAddressMatches: [],
      });
      return;
    }

    dispatch(postWithState, {
      queryId: formValues.addressStreetDisplay,
      action: SR_API_ACTION_GOOGLE_MAPS_GET_AUTOCOMPLETE_PREDICTIONS,
      payload: {
        input: formValues.addressStreetDisplay,
        limitToTexas: false,
      },
    })
      .then((autocompletePredictions) => {
        this.setState({
          googleAddressMatches: autocompletePredictions,
        });
      });
  }

  _onGoogleAddressChange = ({ newValue, formikBag }) => {
    const { googleAddressMatches } = this.state;

    const selectedMatch = (
      googleAddressMatches?.find(({ description }) => description === newValue)
    );
    if (selectedMatch?.place_id) {
      this._loadMatchAddressIntoForm({
        placeId: selectedMatch.place_id,
        formikBag,
      });
    }
    else {
      formikBag.setFieldValue('addressStreetNumber', '');
      formikBag.setFieldValue('addressStreetName', '');
      formikBag.setFieldValue('addressCity', '');
      formikBag.setFieldValue('addressStateCode', '');
      formikBag.setFieldValue('addressZip', '');
      formikBag.setFieldValue('locationLatitude', '');
      formikBag.setFieldValue('locationLongitude', '');
    }
  }

  _loadMatchAddressIntoForm = ({ placeId, formikBag }) => {
    const { dispatch } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_GOOGLE_MAPS_GET_PLACE_DETAILS,
      payload: {
        placeId,
      },
    })
      .then((googlePlace) => {
        const listingMatch = extractListingAddressFieldsFromGooglePlace(googlePlace);
        if (listingMatch) {
          formikBag.setFieldValue('addressStreetNumber', listingMatch.addressStreetNumber);
          formikBag.setFieldValue('addressStreetName', listingMatch.addressStreetName);
          formikBag.setFieldValue('addressCity', listingMatch.addressCity);
          formikBag.setFieldValue('addressStateCode', listingMatch.addressState);
          formikBag.setFieldValue('addressZip', listingMatch.addressZip);
          formikBag.setFieldValue('locationLatitude', parseFloat(listingMatch.locationLatitude) || null);
          formikBag.setFieldValue('locationLongitude', parseFloat(listingMatch.locationLongitude) || null);
        }
      });
  }

  _onFindLandlords = ({ value }) => {
    const { dispatch, partnerId } = this.props;
    dispatch(postWithState, {
      action: SR_API_ACTION_PARTNER_SEARCH_LANDLORDS,
      queryId: `${value}`,
      payload: {
        searchQuery: value,
        partnerId,
      },
    })
      .then((landlordMatches) => {
        if (!this._isUnmounting && landlordMatches) {
          this.setState({
            landlordMatches,
          });
        }
      })
      .catch(log.error);
  }

  _onLandlordChange = ({ newValue, formikBag }) => {
    const { landlordMatches } = this.state;
    const landlordMatch = landlordMatches.find((landlord) => {
      return newValue === makeLandlordMatchString(landlord);
    });
    if (landlordMatch) {
      formikBag.setFieldValue('landlordName', landlordMatch.name);
      formikBag.setFieldValue('landlordPhone', landlordMatch.phone);
      formikBag.setFieldValue('landlordEmail', landlordMatch.email);
    }
  }

  _onOcuppantTypeChange = (occupantType) => {
    const { editMarketingPartnerListing } = this.props;
    const formikBag = this.multiStepFormRef.current.formik.getFormikBag();
    formikBag.setFieldValue('occupantType', occupantType);

    if (
      occupantType === LISTING_OCCUPANT_TYPE_OWNER
      && !formikBag.values.occupantName
      && editMarketingPartnerListing?.landlordName
    ) {
      formikBag.setFieldValue('occupantName', editMarketingPartnerListing?.landlordName);
      formikBag.setFieldValue('occupantEmail', editMarketingPartnerListing?.landlordEmail);
      formikBag.setFieldValue('occupantPhone', editMarketingPartnerListing?.landlordPhone);
    }
    else {
      formikBag.setFieldValue('occupantName', '');
      formikBag.setFieldValue('occupantEmail', '');
      formikBag.setFieldValue('occupantPhone', '');
    }
  }

  _onRequireSunlockChange = (requireSunlock) => {
    const formikBag = this.multiStepFormRef.current.formik.getFormikBag();
    if (!requireSunlock) {
      formikBag.setFieldValue('landlordPropertyAccessNotificationAlert', null);
    }
  }

  _onAccessMethodChange = (accessMethod) => {
    const formikBag = this.multiStepFormRef.current.formik.getFormikBag();
    formikBag.setFieldValue('accessMethod', accessMethod);

    if (accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY) {
      formikBag.setFieldValue('propertySetupAccessType', null);
      formikBag.setFieldValue('propertyAccessCode', null);
    }
    else if (accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE) {
      formikBag.setFieldValue('propertySetupAccessType', PROPERTY_SETUP_ACCESS_TYPE_USE_ACCESS_CODE);
      formikBag.setFieldValue('keyPickupDescription', null);
      formikBag.setFieldValue('lockboxCode', null);
      formikBag.setFieldValue('supraCbsCode', null);
      formikBag.setFieldValue('dateKeyExpectedAtListing', null);
    }
  }

  _onRequireSunlockChange = (requireSunlock) => {
    const formikBag = this.multiStepFormRef.current.formik.getFormikBag();
    if (!requireSunlock) {
      formikBag.setFieldValue('landlordPropertyAccessNotificationAlert', null);
    }
  }

  _onAccessMethodChange = (accessMethod) => {
    const formikBag = this.multiStepFormRef.current.formik.getFormikBag();
    formikBag.setFieldValue('accessMethod', accessMethod);

    if (accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_KEY) {
      formikBag.setFieldValue('propertySetupAccessType', null);
      formikBag.setFieldValue('propertyAccessCode', null);
    }
    else if (accessMethod === PROPERTY_SETUP_ACCESS_METHOD_TYPE_CODE) {
      formikBag.setFieldValue('propertySetupAccessType', PROPERTY_SETUP_ACCESS_TYPE_USE_ACCESS_CODE);
      formikBag.setFieldValue('keyPickupDescription', null);
      formikBag.setFieldValue('lockboxCode', null);
      formikBag.setFieldValue('supraCbsCode', null);
      formikBag.setFieldValue('dateKeyExpectedAtListing', null);
    }
  }

  _onOpenSunlockSelfTouringModal = () => {
    this.setState({
      shouldShowSunlockSelfTouringModal: true,
    });
  }

  _onCloseSunlockSelfTouringModal = () => {
    this.setState({
      shouldShowSunlockSelfTouringModal: false,
    });
  }

  _askForPushNotifications = () => {
    this.props.dispatch(MobileApp.askForPushNotifications);
  }

  _onOpenSupraCbsModal = () => {
    this.setState({
      shouldShowSupraCbsModal: true,
    });
  }

  _onCloseSupraCbsModal = () => {
    this.setState({
      shouldShowSupraCbsModal: false,
    });
  }

  render() {
    const {
      currentUser,
      editMarketingPartnerListing,
      onBack,
      partner,
      partnerId,
      propertyAttributes = [],
      dispatch,
      userAgent,
      helpCards,
      marketsList,
    } = this.props;

    const {
      googleAddressMatches,
      landlordMatches,
      shouldShowSuccessModal,
      shouldShowSunlockSelfTouringModal,
      shouldShowSupraCbsModal,
      shouldShowLoadingScreen,
      isHandsOffFlow,
      formIsSubmitting,
    } = this.state;

    const isMobile = isUserAgentMobile(userAgent);
    const isLandlord = hasRoleLandlord(currentUser.roles);

    const getFormSteps = makeFormStepsGetterFunc({
      isLandlord,
      partner,
      marketingPartnerListing: editMarketingPartnerListing,
      formIsSubmitting,
    });

    const getFormFields = makeFormFieldsGetterFunc({
      isLandlord,
      isMobile,
      googleAddressMatches: googleAddressMatches,
      onGoogleAddressChange: this._onGoogleAddressChange,
      onFindGoogleListings: this._onFindGoogleListings,
      landlordSuggestionChoices: landlordMatches.map(makeLandlordMatchString),
      onFindLandlords: this._onFindLandlords,
      onLandlordChange: this._onLandlordChange,
      onOcuppantTypeChange: this._onOcuppantTypeChange,
      onRequireSunlockChange: this._onRequireSunlockChange,
      onAccessMethodChange: this._onAccessMethodChange,
      onOpenSupraCbsModal: this._onOpenSupraCbsModal,
      onOpenSunlockSelfTouringModal: this._onOpenSunlockSelfTouringModal,
      marketsList,
    });

    if (shouldShowLoadingScreen) {
      return <div className='loading-gif' />;
    }

    const defaultValues = {
      landlordNotificationsEnabled: true,
      agreementForReferralLease: false,
      agreementForLeasingRights: false,
      commissionPercentClientPays: 1.0,
      petFeePerPet: true,
      petDepositPerPet: true,
      monthlyPetRentPerPet: true,
      partnerId: partnerId,
      ...(editMarketingPartnerListing
        ? {
          ...lodashPick(getFormFields().map(({ id }) => id), editMarketingPartnerListing),
          marketingPartnerListingId: editMarketingPartnerListing.id,
        }
        : (
          propertyAttributes.reduce((memo, propertyAttribute) => ({
            ...memo,
            [propertyAttribute.name]: propertyAttribute.attrs.value,
          }), {})
        )
      ),
    };

    if (
      isLandlord && editMarketingPartnerListing?.status === MARKETING_PARTNER_LISTING_STATUS_LANDLORD_INVITED
    ) {
      defaultValues.requireSunlock = true;
      defaultValues.catsAllowed = true;
      defaultValues.dogsAllowed = true;
      defaultValues.maximumPetsAllowed = 2;
      defaultValues.petWeight = PET_WEIGHT_OPTION_ITEM_NONE;
      defaultValues.petFee = 0;
      defaultValues.petFeePerPet = true;
      defaultValues.additionalPetFee = 0;
      defaultValues.petDeposit = 0;
      defaultValues.petDepositPerPet = true;
      defaultValues.additionalPetDeposit = 0;
      defaultValues.monthlyPetRent = 0;
      defaultValues.monthlyPetRentPerPet = true;
      defaultValues.additionalMonthlyPetRent = 0;
    }

    return (
      <>
        <SrMultiStepForm
          ref={this.multiStepFormRef}
          getFormSteps={getFormSteps}
          getFormFields={getFormFields}
          onBack={onBack}
          onSubmit={this._onSubmit}
          defaultValues={defaultValues}
          defaultStep={(
            editMarketingPartnerListing
            && (
              editMarketingPartnerListing.status === MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS
              || (editMarketingPartnerListing.status === MARKETING_PARTNER_LISTING_STATUS_LANDLORD_INVITED && !isLandlord) // eslint-disable-line max-len
            )
          )
            ? getFormSteps({ values: editMarketingPartnerListing }).length - 1
            : null
          }
          confirmLeave={!shouldShowSuccessModal}
          dispatch={dispatch}
          partner={partner}
          helpCards={helpCards}
          validateOnClickNext
        />
        {shouldShowSuccessModal && (
          <SrSimpleMessageModal
            iconUrl='/images/checkmark-circle-large-green.svg'
            headline='Thank you for listing with Sunroom!'
            underline={isHandsOffFlow && !isLandlord}
            message={isHandsOffFlow && !isLandlord
              ? 'Your client was just invited via email to get the listing live. ' +
              'You\'ll be notified when the property goes live.' : null}
            multiButtons={isLandlord ? [] : lodashCompact([
              !editMarketingPartnerListing && {
                buttonText: 'Market Another Property',
                buttonTheme: BUTTON_THEME_GREY_ADOBE,
                onButtonClick: this._onSuccessModalMarketAnotherProperty,
              },
              {
                buttonText: 'Return Home',
                buttonTheme: BUTTON_THEME_GREEN_ADOBE,
                onButtonClick: this._onSuccessModalReturnHome,
              },
            ])}
            maxWidth='375'
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          />
        )}
        {shouldShowSunlockSelfTouringModal && (
          <SrSunlocksSelfTouringModal onClose={this._onCloseSunlockSelfTouringModal} />
        )}
        {shouldShowSupraCbsModal && (
          <SrSupraCbsModal onClose={this._onCloseSupraCbsModal} />
        )}
      </>
    );
  }
}

SrPartnerMarketListingForm.displayName = 'SrPartnerMarketListingForm';

export default SrPartnerMarketListingForm;
